import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Tooltip,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import {
  ClientApartment,
  AptExpense,
  AptExpenseCategory,
  AptIncome,
  AptIncomeCategory,
  ApartmentCurrency,
  createAptExpense,
  updateAptExpense,
  deleteAptExpense,
  createAptIncome,
  updateAptIncome,
  deleteAptIncome,
} from "../../../utils/api/apartmentRentalsAPI";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${new Intl.NumberFormat("de-DE").format(Math.round(n))}`;
  if (currency === "BAM") return `${new Intl.NumberFormat("bs-BA").format(Math.round(n))} KM`;
  return `${new Intl.NumberFormat("sr-RS").format(Math.round(n))} RSD`;
};

const fmtDate = (d: string) => dayjs(d).format("DD.MM.YYYY");

const EXPENSE_CATEGORY_LABEL: Record<AptExpenseCategory, string> = {
  maintenance: "Popravke / Servis",
  cleaning: "Čišćenje",
  utilities: "Struja / Voda / Internet",
  supplies: "Potrošni materijal",
  commission: "Provizija (Booking, Airbnb)",
  tax: "Porezi / Takse",
  marketing: "Marketing",
  insurance: "Osiguranje",
  other: "Ostalo",
};

const EXPENSE_CATEGORY_ICON: Record<AptExpenseCategory, string> = {
  maintenance: "🔧",
  cleaning: "🧹",
  utilities: "💡",
  supplies: "📦",
  commission: "💼",
  tax: "🏛",
  marketing: "📣",
  insurance: "🛡",
  other: "📎",
};

const INCOME_CATEGORY_LABEL: Record<AptIncomeCategory, string> = {
  quad: "Quad / Buggy / ATV",
  food: "Hrana",
  drinks: "Pića",
  transfer: "Transfer / Prijevoz",
  tour: "Izlet / Tura",
  equipment: "Oprema / Najam",
  sport: "Sportske aktivnosti",
  other: "Ostalo",
};

const INCOME_CATEGORY_ICON: Record<AptIncomeCategory, string> = {
  quad: "🏍",
  food: "🍽",
  drinks: "🥤",
  transfer: "🚗",
  tour: "🗺",
  equipment: "⛷",
  sport: "⚽",
  other: "💰",
};

const CURRENCIES: ApartmentCurrency[] = ["RSD", "EUR", "BAM"];

// ─── Expense Modal ─────────────────────────────────────────────────────────────

interface ExpenseModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editData: AptExpense | null;
  clientId: string;
  apartments: ClientApartment[];
}

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  open, onClose, onSaved, editData, clientId, apartments,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        const aptId = typeof editData.apartmentId === "object"
          ? (editData.apartmentId as any)?._id ?? null
          : editData.apartmentId ?? null;
        form.setFieldsValue({
          ...editData,
          date: dayjs(editData.date),
          apartmentId: aptId,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ currency: "EUR", category: "other" });
      }
    }
  }, [open, editData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        date: values.date?.toISOString(),
        apartmentId: values.apartmentId || null,
      };
      if (editData) {
        await updateAptExpense(editData._id, payload);
      } else {
        await createAptExpense(clientId, payload);
      }
      onSaved();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error("Greška pri čuvanju");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editData ? "Izmijeni rashod" : "Dodaj rashod"}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={editData ? "Sačuvaj" : "Dodaj"}
      cancelText="Otkaži"
      confirmLoading={saving}
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="date" label="Datum" rules={[{ required: true, message: "Obavezno" }]}>
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>

        <Form.Item name="category" label="Kategorija" rules={[{ required: true }]}>
          <Select>
            {(Object.keys(EXPENSE_CATEGORY_LABEL) as AptExpenseCategory[]).map((k) => (
              <Select.Option key={k} value={k}>
                {EXPENSE_CATEGORY_ICON[k]} {EXPENSE_CATEGORY_LABEL[k]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Opis" rules={[{ required: true, message: "Obavezno" }]}>
          <Input placeholder="npr. Popravka bojlera, čišćenje apartmana..." />
        </Form.Item>

        <div style={{ display: "flex", gap: 12 }}>
          <Form.Item name="amount" label="Iznos" rules={[{ required: true, message: "Obavezno" }]} style={{ flex: 1 }}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="currency" label="Valuta" initialValue="EUR" style={{ width: 90 }}>
            <Select>
              {CURRENCIES.map((c) => <Select.Option key={c} value={c}>{c}</Select.Option>)}
            </Select>
          </Form.Item>
        </div>

        <Form.Item name="apartmentId" label="Apartman (opcionalno)">
          <Select allowClear placeholder="Svi apartmani (opće)">
            {apartments.map((a) => (
              <Select.Option key={a._id} value={a._id}>{a.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="note" label="Napomena">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Income Modal ──────────────────────────────────────────────────────────────

interface IncomeModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editData: AptIncome | null;
  clientId: string;
  apartments: ClientApartment[];
}

const IncomeModal: React.FC<IncomeModalProps> = ({
  open, onClose, onSaved, editData, clientId, apartments,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        const aptId = typeof editData.apartmentId === "object"
          ? (editData.apartmentId as any)?._id ?? null
          : editData.apartmentId ?? null;
        form.setFieldsValue({
          ...editData,
          date: dayjs(editData.date),
          apartmentId: aptId,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ currency: "EUR", category: "other", isPaid: true });
      }
    }
  }, [open, editData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        date: values.date?.toISOString(),
        apartmentId: values.apartmentId || null,
      };
      if (editData) {
        await updateAptIncome(editData._id, payload);
      } else {
        await createAptIncome(clientId, payload);
      }
      onSaved();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error("Greška pri čuvanju");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editData ? "Izmijeni dodatni prihod" : "Dodaj dodatni prihod"}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={editData ? "Sačuvaj" : "Dodaj"}
      cancelText="Otkaži"
      confirmLoading={saving}
      width={480}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="date" label="Datum" rules={[{ required: true, message: "Obavezno" }]}>
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>

        <Form.Item name="category" label="Kategorija" rules={[{ required: true }]}>
          <Select>
            {(Object.keys(INCOME_CATEGORY_LABEL) as AptIncomeCategory[]).map((k) => (
              <Select.Option key={k} value={k}>
                {INCOME_CATEGORY_ICON[k]} {INCOME_CATEGORY_LABEL[k]}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Opis" rules={[{ required: true, message: "Obavezno" }]}>
          <Input placeholder="npr. Quad rental 2h, večera za 4 osobe..." />
        </Form.Item>

        <div style={{ display: "flex", gap: 12 }}>
          <Form.Item name="amount" label="Iznos" rules={[{ required: true, message: "Obavezno" }]} style={{ flex: 1 }}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="currency" label="Valuta" initialValue="EUR" style={{ width: 90 }}>
            <Select>
              {CURRENCIES.map((c) => <Select.Option key={c} value={c}>{c}</Select.Option>)}
            </Select>
          </Form.Item>
        </div>

        <Form.Item name="apartmentId" label="Poveži s apartmanom (opcionalno)">
          <Select allowClear placeholder="Nije vezano za apartman">
            {apartments.map((a) => (
              <Select.Option key={a._id} value={a._id}>{a.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="note" label="Napomena">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

interface FinanceTabProps {
  clientId: string;
  apartments: ClientApartment[];
  /** Revenue from reservations for the selected month (already computed in parent) */
  reservationRevenue: Record<string, { total: number; paid: number; pending: number }>;
  /** Additional income/expenses for the selected month (already loaded in parent) */
  incomes: AptIncome[];
  expenses: AptExpense[];
  loading: boolean;
  /** Reload incomes/expenses in the parent after add/edit/delete */
  onChanged: () => void;
}

const FinanceTab: React.FC<FinanceTabProps> = ({
  clientId, apartments, reservationRevenue, incomes, expenses, loading, onChanged,
}) => {
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<AptExpense | null>(null);
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState<AptIncome | null>(null);

  // ─ Summary numbers, grouped by currency (same shape as reservationRevenue) ─
  const summary = useMemo(() => {
    const byCurrency: Record<string, { reservation: number; income: number; expense: number; net: number }> = {};
    const ensure = (currency: string) => {
      if (!byCurrency[currency]) byCurrency[currency] = { reservation: 0, income: 0, expense: 0, net: 0 };
      return byCurrency[currency];
    };

    Object.entries(reservationRevenue).forEach(([currency, rev]) => {
      ensure(currency).reservation += rev.total;
    });
    expenses.forEach((e) => { ensure(e.currency).expense += e.amount || 0; });
    incomes.forEach((i) => { ensure(i.currency).income += i.amount || 0; });
    Object.values(byCurrency).forEach((v) => { v.net = v.reservation + v.income - v.expense; });

    return {
      totalExpensesByCurrency: Object.fromEntries(
        Object.entries(byCurrency).map(([c, v]) => [c, v.expense])
      ),
      totalAdditionalIncomeByCurrency: Object.fromEntries(
        Object.entries(byCurrency).map(([c, v]) => [c, v.income])
      ),
      netProfitByCurrency: Object.fromEntries(
        Object.entries(byCurrency).map(([c, v]) => [c, v.net])
      ),
    };
  }, [expenses, incomes, reservationRevenue]);

  // ─ Expense columns ─
  const expenseColumns: ColumnsType<AptExpense> = [
    {
      title: "Datum",
      key: "date",
      width: 110,
      render: (_, r) => <span style={{ color: "#6b6880", fontSize: 13 }}>{fmtDate(r.date)}</span>,
    },
    {
      title: "Kategorija",
      key: "category",
      render: (_, r) => (
        <span>
          {EXPENSE_CATEGORY_ICON[r.category]}{" "}
          <span style={{ fontSize: 13 }}>{EXPENSE_CATEGORY_LABEL[r.category]}</span>
        </span>
      ),
    },
    {
      title: "Opis",
      dataIndex: "description",
      key: "description",
      render: (v) => <span style={{ fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Apartman",
      key: "apartment",
      render: (_, r) => {
        const name = typeof r.apartmentId === "object"
          ? (r.apartmentId as any)?.name
          : apartments.find((a) => a._id === r.apartmentId)?.name;
        return <span style={{ fontSize: 13, color: name ? "#3d3650" : "#9893a8" }}>{name || "Opće"}</span>;
      },
    },
    {
      title: "Iznos",
      key: "amount",
      align: "right" as const,
      render: (_, r) => (
        <span style={{ fontWeight: 600, color: "#dc2626", fontSize: 13 }}>
          -{fmtAmount(r.amount, r.currency)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tooltip title="Izmijeni">
            <button
              className="ar-apt-cover-btn"
              style={{ background: "#fff", border: "1px solid #e8e5f0" }}
              onClick={() => { setEditingExpense(r); setExpenseModalOpen(true); }}
            >
              <EditOutlined style={{ fontSize: 11 }} />
            </button>
          </Tooltip>
          <Popconfirm title="Izbriši rashod?" onConfirm={async () => {
            await deleteAptExpense(r._id);
            message.success("Rashod izbrisan");
            onChanged();
          }} okText="Da" cancelText="Ne">
            <Tooltip title="Izbriši">
              <button className="ar-apt-cover-btn" style={{ background: "#fff", border: "1px solid #e8e5f0" }}>
                <DeleteOutlined style={{ fontSize: 11 }} />
              </button>
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // ─ Income columns ─
  const incomeColumns: ColumnsType<AptIncome> = [
    {
      title: "Datum",
      key: "date",
      width: 110,
      render: (_, r) => <span style={{ color: "#6b6880", fontSize: 13 }}>{fmtDate(r.date)}</span>,
    },
    {
      title: "Kategorija",
      key: "category",
      render: (_, r) => (
        <span>
          {INCOME_CATEGORY_ICON[r.category]}{" "}
          <span style={{ fontSize: 13 }}>{INCOME_CATEGORY_LABEL[r.category]}</span>
        </span>
      ),
    },
    {
      title: "Opis",
      dataIndex: "description",
      key: "description",
      render: (v) => <span style={{ fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Apartman",
      key: "apartment",
      render: (_, r) => {
        const name = typeof r.apartmentId === "object"
          ? (r.apartmentId as any)?.name
          : apartments.find((a) => a._id === r.apartmentId)?.name;
        return <span style={{ fontSize: 13, color: name ? "#3d3650" : "#9893a8" }}>{name || "Van apartmana"}</span>;
      },
    },
    {
      title: "Iznos",
      key: "amount",
      align: "right" as const,
      render: (_, r) => (
        <span style={{ fontWeight: 600, color: "#16a34a", fontSize: 13 }}>
          +{fmtAmount(r.amount, r.currency)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tooltip title="Izmijeni">
            <button
              className="ar-apt-cover-btn"
              style={{ background: "#fff", border: "1px solid #e8e5f0" }}
              onClick={() => { setEditingIncome(r); setIncomeModalOpen(true); }}
            >
              <EditOutlined style={{ fontSize: 11 }} />
            </button>
          </Tooltip>
          <Popconfirm title="Izbriši prihod?" onConfirm={async () => {
            await deleteAptIncome(r._id);
            message.success("Prihod izbrisan");
            onChanged();
          }} okText="Da" cancelText="Ne">
            <Tooltip title="Izbriši">
              <button className="ar-apt-cover-btn" style={{ background: "#fff", border: "1px solid #e8e5f0" }}>
                <DeleteOutlined style={{ fontSize: 11 }} />
              </button>
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="ar-finance-tab">
      {/* ─ Summary strip ─ */}
      <div className="ar-finance-summary">
        <div className="ar-finance-kpi ar-finance-kpi--revenue">
          <div className="ar-finance-kpi-icon">🏨</div>
          <div className="ar-finance-kpi-body">
            <div className="ar-finance-kpi-label">Prihodi od rezervacija</div>
            <div className="ar-finance-kpi-value">
              {Object.keys(reservationRevenue).length === 0
                ? "0"
                : Object.entries(reservationRevenue).map(([currency, rev]) => (
                    <span key={currency} style={{ display: "block" }}>
                      {fmtAmount(rev.total, currency)}
                    </span>
                  ))}
            </div>
          </div>
        </div>

        <div className="ar-finance-kpi ar-finance-kpi--income">
          <div className="ar-finance-kpi-icon">💰</div>
          <div className="ar-finance-kpi-body">
            <div className="ar-finance-kpi-label">Dodatni prihodi</div>
            <div className="ar-finance-kpi-value">
              {incomes.length === 0
                ? "0"
                : Object.entries(summary.totalAdditionalIncomeByCurrency).map(([currency, amount]) => (
                    <span key={currency} style={{ display: "block" }}>
                      {fmtAmount(amount, currency)}
                    </span>
                  ))}
            </div>
            <div className="ar-finance-kpi-sub">{incomes.length} stavki</div>
          </div>
        </div>

        <div className="ar-finance-kpi ar-finance-kpi--expense">
          <div className="ar-finance-kpi-icon">📉</div>
          <div className="ar-finance-kpi-body">
            <div className="ar-finance-kpi-label">Ukupni rashodi</div>
            <div className="ar-finance-kpi-value">
              {expenses.length === 0
                ? "0"
                : Object.entries(summary.totalExpensesByCurrency).map(([currency, amount]) => (
                    <span key={currency} style={{ display: "block" }}>
                      {fmtAmount(amount, currency)}
                    </span>
                  ))}
            </div>
            <div className="ar-finance-kpi-sub">{expenses.length} stavki</div>
          </div>
        </div>

        <div className={`ar-finance-kpi ar-finance-kpi--profit${Object.values(summary.netProfitByCurrency).some((v) => v < 0) ? " ar-finance-kpi--loss" : ""}`}>
          <div className="ar-finance-kpi-icon">
            {Object.values(summary.netProfitByCurrency).some((v) => v < 0) ? "📉" : "📈"}
          </div>
          <div className="ar-finance-kpi-body">
            <div className="ar-finance-kpi-label">Realna zarada</div>
            <div className="ar-finance-kpi-value ar-finance-kpi-value--profit">
              {Object.keys(summary.netProfitByCurrency).length === 0
                ? "0"
                : Object.entries(summary.netProfitByCurrency).map(([currency, amount]) => (
                    <span key={currency} style={{ display: "block" }}>
                      {amount >= 0 ? "+" : ""}{fmtAmount(amount, currency)}
                    </span>
                  ))}
            </div>
            <div className="ar-finance-kpi-sub">
              prihodi − rashodi
            </div>
          </div>
        </div>
      </div>

      {/* ─ Expenses section ─ */}
      <div className="ar-finance-section">
        <div className="ar-finance-section-head">
          <div className="ar-finance-section-title">
            <span className="ar-finance-section-dot ar-finance-section-dot--expense" />
            Rashodi
            <span className="ar-tab-count">{expenses.length}</span>
          </div>
          <button
            className="ar-btn ar-btn--primary"
            onClick={() => { setEditingExpense(null); setExpenseModalOpen(true); }}
          >
            <PlusOutlined /> Dodaj rashod
          </button>
        </div>

        {expenses.length === 0 ? (
          <div className="ar-empty" style={{ padding: "32px 0" }}>
            <div style={{ fontSize: 36, opacity: 0.4 }}>📉</div>
            <div className="t">Nema rashoda za ovaj period</div>
            <div className="s">Evidentirajte troškove: čišćenje, popravke, provizije...</div>
          </div>
        ) : (
          <Table
            columns={expenseColumns}
            dataSource={expenses}
            rowKey="_id"
            size="small"
            pagination={false}
            scroll={{ x: 600 }}
          />
        )}
      </div>

      {/* ─ Additional income section ─ */}
      <div className="ar-finance-section">
        <div className="ar-finance-section-head">
          <div className="ar-finance-section-title">
            <span className="ar-finance-section-dot ar-finance-section-dot--income" />
            Dodatni prihodi
            <span className="ar-tab-count">{incomes.length}</span>
          </div>
          <button
            className="ar-btn ar-btn--primary"
            onClick={() => { setEditingIncome(null); setIncomeModalOpen(true); }}
          >
            <PlusOutlined /> Dodaj prihod
          </button>
        </div>
        <p className="ar-finance-section-hint">
          Prihodi koji nisu direktno rezervacija — quad, hrana, pića, izleti itd.
          Mogu biti vezani za određeni apartman ili slobodni (van apartmana).
        </p>

        {incomes.length === 0 ? (
          <div className="ar-empty" style={{ padding: "32px 0" }}>
            <div style={{ fontSize: 36, opacity: 0.4 }}>💰</div>
            <div className="t">Nema dodatnih prihoda za ovaj period</div>
            <div className="s">Evidentirajte: quad rental, hrana, pića, transfer, izleti...</div>
          </div>
        ) : (
          <Table
            columns={incomeColumns}
            dataSource={incomes}
            rowKey="_id"
            size="small"
            pagination={false}
            scroll={{ x: 600 }}
          />
        )}
      </div>

      {/* Modals */}
      <ExpenseModal
        open={expenseModalOpen}
        onClose={() => setExpenseModalOpen(false)}
        onSaved={() => { setExpenseModalOpen(false); onChanged(); message.success(editingExpense ? "Rashod ažuriran" : "Rashod dodan"); }}
        editData={editingExpense}
        clientId={clientId}
        apartments={apartments}
      />
      <IncomeModal
        open={incomeModalOpen}
        onClose={() => setIncomeModalOpen(false)}
        onSaved={() => { setIncomeModalOpen(false); onChanged(); message.success(editingIncome ? "Prihod ažuriran" : "Prihod dodan"); }}
        editData={editingIncome}
        clientId={clientId}
        apartments={apartments}
      />
    </div>
  );
};

export default FinanceTab;
